import { useMemo } from 'react';
import { memoizeWeak } from '~/_shared/utils/memoize/memoize';
import { defaultBoundaryTerritoryGroupSettings } from '~/boundary/settings/defaultBoundarySettings';
import { type BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { useBoundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useActiveBoundaryTerritoryGroupsSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { type BoundaryTerritoryGroupStyle } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useMapComponentZoomSelector } from '~/store/frontendState/mapComponent/mapComponent.selectors';
import { useBoundarySelectModalOpenSelector } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.selectors';
import { useMapSettingsBoundariesPrimaryBoundaryGroupIdSelector } from '~/store/mapSettings/boundaries/mapSettingsBoundaries.selectors';
import { type BoundaryItemStyle } from '../../mapBoundary.manager';
import { hideBoundaryBordersOnZoomLevel } from '../mapBoundary.helpers';
import { useMapBoundaryZIndexes } from './useMapBoundaryZIndexes';

const defaultBoundaryStyle: BoundaryItemStyle = {
  fillColor: '#000',
  fillOpacity: { value: 0 },
  borderColor: { value: '#000' },
  borderOpacity: { value: 1 },
  borderWidth: .5,
  labelBackgroundColor: defaultBoundaryTerritoryGroupSettings.labelStyle.backgroundColor,
  labelFontColor: defaultBoundaryTerritoryGroupSettings.labelStyle.fontColor,
  zIndex: {
    polygon: 0,
    border: 1,
    label: 2,
  },
  showPolygonOutline: false,
  interactive: true,
};

export const useMapBoundaryBaseStyles = () => {
  const boundaryGroups = useBoundaryGroupsSelector();
  const activeBoundaryTerritoryGroups = useActiveBoundaryTerritoryGroupsSelector();
  const primaryBoundaryGroupId = useMapSettingsBoundariesPrimaryBoundaryGroupIdSelector();
  const zIndexes = useMapBoundaryZIndexes();
  const mapZoom = useMapComponentZoomSelector();
  const boundarySelect = useBoundarySelectModalOpenSelector();

  return useMemo<ReadonlyMap<BoundaryGroupId, BoundaryItemStyle>>(() => {
    const result = new Map<BoundaryGroupId, BoundaryItemStyle>();

    if (!mapZoom) {
      return result;
    }

    for (const boundaryTerritoryGroup of activeBoundaryTerritoryGroups) {
      const boundaryGroup = boundaryGroups.find(g => g.id === boundaryTerritoryGroup.boundaryGroupId);
      if (!boundaryGroup) {
        continue;
      }

      const isPrimary = boundaryTerritoryGroup.boundaryGroupId === primaryBoundaryGroupId;
      const zIndex = isPrimary ? zIndexes.primaryBoundary : zIndexes[boundaryTerritoryGroup.boundaryGroupId] ?? defaultBoundaryStyle.zIndex;

      const boundaryUsedInSelect = boundarySelect?.selectBoundaryGroupId === boundaryGroup.id;
      const hideBorders = hideBoundaryBordersOnZoomLevel(mapZoom, boundaryGroup, boundaryTerritoryGroup) || boundaryUsedInSelect;
      const isBoundarySelectActive = !!boundarySelect?.isActive;

      // Memoization ensures styles are the same object unless inputs have changed
      const styles = getBoundaryBaseStylesMemoized(
        boundaryTerritoryGroup.settings.style,
        zIndex.polygon,
        zIndex.border,
        zIndex.label,
        hideBorders,
        isBoundarySelectActive,
        !boundaryTerritoryGroup.settings.disableMapInteraction,
        boundaryTerritoryGroup.settings.labelStyle.fontColor,
        boundaryTerritoryGroup.settings.labelStyle.backgroundColor,
      );
      result.set(boundaryTerritoryGroup.boundaryGroupId, styles);
    }

    return result;
  },
  [mapZoom, activeBoundaryTerritoryGroups, boundaryGroups, primaryBoundaryGroupId, zIndexes,
    boundarySelect?.selectBoundaryGroupId, boundarySelect?.isActive]);
};

const getBoundaryBaseStyles = (
  style: BoundaryTerritoryGroupStyle,
  polygonZIndex: number,
  borderZIndex: number,
  labelZIndex: number,
  hideBorders: boolean,
  isBoundarySelectActive: boolean,
  interactive: boolean,
  fontColor: string,
  backgroundColor: string
): BoundaryItemStyle => ({
  ...defaultBoundaryStyle,
  borderWidth: hideBorders ? 0 : style.lineWidth,
  borderColor: { value: style.lineColor },
  borderOpacity: hideBorders ? { value: 0 } : (isBoundarySelectActive ? { value: .2 } : { value: 1 }),
  zIndex: {
    polygon: polygonZIndex,
    border: borderZIndex,
    label: labelZIndex,
  },
  interactive,
  labelBackgroundColor: backgroundColor,
  labelFontColor: fontColor,
});
const getBoundaryBaseStylesMemoized = memoizeWeak(getBoundaryBaseStyles);
